import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTryOnAsyncMutation, useGetVtoStatusQuery, useGetMyPersonImagesQuery, useGetVtoResultsByGarmentQuery, useCheckLicenseQuery } from '../../redux/apis';
import { useGetSharedClosetCreditCostQuery } from '../../redux/apis/endpoints/paymentApi';
import { getThumbnailUrl } from '../../utils/imageUtils';
import { VTOResultPanel, ExistingResultsGrid, useCreditBalance, getProductImageUrl, PersonPlaceholderSVG } from './VTOResultView';
import { BACKEND_URL } from '../../utils/config';
import CreditPurchaseModal from '../credits/CreditPurchaseModal';
import toast from 'react-hot-toast';

const extractImageUrls = (resultObj) => {
    if (!resultObj) return { mainImgUrl: '', allImages: [] };
    let mainImgUrl = resultObj.imageUrl || resultObj.resultImage;
    if (Array.isArray(mainImgUrl)) mainImgUrl = mainImgUrl[mainImgUrl.length - 1];
    if (mainImgUrl && typeof mainImgUrl === 'object') mainImgUrl = mainImgUrl.url;
    let allImages = resultObj.allImages || resultObj.backendResult?.allImages;
    if (!allImages) {
        if (Array.isArray(resultObj.resultImage)) {
            allImages = resultObj.resultImage.map(img => typeof img === 'object' ? img.url : img).filter(Boolean);
        } else if (mainImgUrl) {
            allImages = [mainImgUrl];
        } else {
            allImages = [];
        }
    }
    if (Array.isArray(allImages)) {
        allImages = allImages.map(img => typeof img === 'object' ? img.url : img).filter(Boolean);
    }
    return { mainImgUrl, allImages };
};

const VirtualTryOnModal = forwardRef(({ isOpen, onClose, product, closetClothesId, onPurchaseLicense }, ref) => {
    const token = useSelector(state => state.auth?.token);
    const isLoggedIn = !!token;
    const history = useHistory();

    
    const { data: licenseCheck, refetch: refetchLicense } = useCheckLicenseQuery(
        { listingId: product?.id, itemType: 'product' },
        { skip: !isLoggedIn || !product?.licensingEnabled || !product?.id }
    );
    const hasLicense = licenseCheck?.hasLicense || false;

    const [selectedPersonImage, setSelectedPersonImage] = useState(null);
    const [jobId, setJobId] = useState(null);
    const [vtoResult, setVtoResult] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [forceNewTryOn, setForceNewTryOn] = useState(false);
    const [selectedExistingResult, setSelectedExistingResult] = useState(null);
    const overlayRef = useRef(null);
    const fileInputRef = useRef(null);
    const [isCreditModalOpen, setIsCreditModalOpen] = useState(false);

    // Fetch existing VTO results for this garment
    const { data: existingResults = [], isLoading: loadingExisting, refetch: refetchExisting } = useGetVtoResultsByGarmentQuery(
        closetClothesId,
        { skip: !isLoggedIn || !isOpen || !closetClothesId }
    );
    const hasExistingResults = existingResults.length > 0 && !forceNewTryOn;

    const { data: personImages = [], isLoading: loadingImages } = useGetMyPersonImagesQuery(undefined, { skip: !isLoggedIn || !isOpen });
    const [tryOnAsync, { isLoading: isStarting }] = useTryOnAsyncMutation();
    const { balance, loading: loadingCredits, refetch: refetchCredits } = useCreditBalance();

    // Expose imperative handle so parent can trigger refetches after license purchase
    useImperativeHandle(ref, () => ({
        onLicensePurchased: async () => {
            try {
                await refetchLicense();
                await refetchExisting();
            } catch (e) {
                // Ignore if queries haven't started
            }
        }
    }), [refetchLicense, refetchExisting]);

    const { data: statusData } = useGetVtoStatusQuery(jobId, {
        skip: !jobId || vtoResult !== null,
        pollingInterval: jobId && !vtoResult ? 3000 : 0
    });

    const { data: creditCostData } = useGetSharedClosetCreditCostQuery(
        closetClothesId,
        { skip: !isLoggedIn || !isOpen || !closetClothesId }
    );

    const isSharedItem = creditCostData?.isShared ?? false;
    const sharerUsername = creditCostData?.ownerUsername ?? null;
    const VTO_COST = creditCostData?.totalCost ?? 1;
    const garmentImageUrl = getProductImageUrl(product);

    // Auto-select first person image
    useEffect(() => {
        if (personImages.length > 0 && !selectedPersonImage) {
            setSelectedPersonImage(personImages[0]);
        }
    }, [personImages, selectedPersonImage]);

    // Auto-select existing result if there is only 1
    useEffect(() => {
        if (existingResults.length === 1 && !selectedExistingResult && !forceNewTryOn && !vtoResult && !isStarting && !jobId) {
            setSelectedExistingResult(existingResults[0]);
        }
    }, [existingResults, selectedExistingResult, forceNewTryOn, vtoResult, isStarting, jobId]);

    // Manually trigger refetch when license is purchased
    // (licensingApi and vtoApi are separate createApi slices, so cross-API tag invalidation won't work natively)
    const prevHasLicense = useRef(hasLicense);
    useEffect(() => {
        if (!prevHasLicense.current && hasLicense && isOpen && closetClothesId) {
            try {
                refetchExisting();
            } catch (e) {
                // Ignore error if query hasn't started yet
            }
        }
        prevHasLicense.current = hasLicense;
    }, [hasLicense, isOpen, closetClothesId, refetchExisting]);

    // Sync local result state with fresh data from RTK Query (e.g. after a license purchase strips the watermark)
    useEffect(() => {
        setVtoResult(prev => {
            if (!prev || !prev.savedResultId) return prev;
            const match = existingResults.find(r => r.id === prev.savedResultId);
            if (match && (match.imageUrl !== prev.imageUrl || JSON.stringify(match.allImages) !== JSON.stringify(prev.allImages))) {
                return { ...prev, imageUrl: match.imageUrl, allImages: match.allImages || prev.allImages };
            }
            return prev;
        });

        setSelectedExistingResult(prev => {
            if (!prev) return prev;
            const match = existingResults.find(r => r.id === prev.id);
            if (match && (match.imageUrl !== prev.imageUrl || JSON.stringify(match.allImages) !== JSON.stringify(prev.allImages))) {
                return match;
            }
            return prev;
        });
    }, [existingResults]);

    // Handle VTO status updates - backend returns result.imageUrl (string URL)
    useEffect(() => {
        if (statusData) {
            if (statusData.status === 'completed' && statusData.result) {
                const { mainImgUrl, allImages } = extractImageUrls(statusData.result);
                setVtoResult({
                    imageUrl: getThumbnailUrl(mainImgUrl, 300),
                    savedResultId: statusData.result.savedResultId,
                    creditBalance: statusData.result.creditBalance,
                    cached: !!statusData.cached || !!statusData.result.cached,
                    allImages
                });
                setJobId(null);
                refetchCredits();
                refetchExisting();
            } else if (statusData.status === 'failed') {
                toast.error('Virtual Try-On failed. Please try again.');
                setJobId(null);
                refetchCredits();
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusData]);

    // Close on overlay click
    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) handleClose();
    };

    // Close on Escape + lock body scroll
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') handleClose(); };
        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Handle file upload from input or drop
    const handleFileUpload = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }
        if (!isLoggedIn) {
            history.push(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            return;
        }

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('image', file);
            formData.append('fileName', file.name);

            const res = await fetch(`${BACKEND_URL}/person-images/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });

            if (res.ok) {
                const data = await res.json();
                if (data.data) {
                    setSelectedPersonImage(data.data);
                    toast.success('Photo uploaded!');
                }
            } else {
                toast.error('Failed to upload photo');
            }
        } catch (err) {
            toast.error('Failed to upload photo');
        } finally {
            setIsUploading(false);
        }
    };

    // Drag & drop handlers
    const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation(); setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileUpload(file);
    };

    // Person area click
    const handlePersonAreaClick = () => {
        if (!isLoggedIn) {
            history.push(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            return;
        }
        fileInputRef.current?.click();
    };

    const handleStartTryOn = async () => {
        if (!isLoggedIn) {
            history.push(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            return;
        }
        if (!selectedPersonImage) { toast.error('Please select or upload a photo first'); return; }
        if (balance !== null && balance < VTO_COST) { setIsCreditModalOpen(true); return; }
        if (!garmentImageUrl) { toast.error('Product image is missing'); return; }

        try {
            const response = await tryOnAsync({
                personImage: selectedPersonImage.image?.url || selectedPersonImage.originalImageUrl,
                garmentImage: garmentImageUrl,
                options: {
                    useHD: true,
                    closetClothesId,
                    garmentId: closetClothesId,
                    garmentProductId: product?.id,
                    originalPersonImageId: selectedPersonImage.id,
                    personImageId: selectedPersonImage.id,
                    personImageRecordId: selectedPersonImage.id
                }
            }).unwrap();

            // Handle immediate cache hit (status=completed, no polling needed)
            if (response.status === 'completed' && response.result) {
                const { mainImgUrl, allImages } = extractImageUrls(response.result);
                setVtoResult({
                    imageUrl: getThumbnailUrl(mainImgUrl, 300),
                    savedResultId: response.result.savedResultId,
                    creditBalance: response.result.creditBalance,
                    cached: !!response.cached || !!response.result.cached,
                    allImages
                });
                refetchCredits();
                refetchExisting();
            } else if (response.success && response.jobId) {
                setJobId(response.jobId);
            }
        } catch (error) {
            toast.error('Failed to start Virtual Try-On');
        }
    };

    const handleClose = () => {
        setJobId(null);
        setVtoResult(null);
        setForceNewTryOn(false);
        setSelectedExistingResult(null);
        onClose();
    };

    const handleTryNewPhoto = () => {
        setForceNewTryOn(true);
        setSelectedExistingResult(null);
        setVtoResult(null);
    };

    const handleBackToResults = () => {
        setForceNewTryOn(false);
        setSelectedExistingResult(null);
        setVtoResult(null);
    };

    // Redo: delete old cached result and regenerate
    const handleRedoTryOn = async () => {
        // Determine which person image was used for this result
        const redoPersonImage = selectedExistingResult?.personImageId
            ? personImages.find(p => p.id === selectedExistingResult.personImageId) || selectedPersonImage
            : selectedPersonImage;

        if (!redoPersonImage) {
            toast.error('Please select a photo first');
            setForceNewTryOn(true);
            setSelectedExistingResult(null);
            setVtoResult(null);
            return;
        }
        if (balance !== null && balance < VTO_COST) {
            setIsCreditModalOpen(true);
            return;
        }

        // Reset result state so processing overlay shows
        setVtoResult(null);
        setSelectedExistingResult(null);

        try {
            const response = await tryOnAsync({
                personImage: redoPersonImage.image?.url || redoPersonImage.originalImageUrl,
                garmentImage: garmentImageUrl,
                options: {
                    useHD: true,
                    closetClothesId,
                    garmentId: closetClothesId,
                    garmentProductId: product?.id,
                    originalPersonImageId: redoPersonImage.id,
                    personImageId: redoPersonImage.id,
                    personImageRecordId: redoPersonImage.id,
                    forceRegenerate: true
                }
            }).unwrap();

            if (response.status === 'completed' && response.result) {
                const { mainImgUrl, allImages } = extractImageUrls(response.result);
                setVtoResult({
                    imageUrl: getThumbnailUrl(mainImgUrl, 300),
                    savedResultId: response.result.savedResultId,
                    creditBalance: response.result.creditBalance,
                    cached: false,
                    allImages
                });
                refetchCredits();
                refetchExisting();
            } else if (response.success && response.jobId) {
                setJobId(response.jobId);
            }
        } catch (error) {
            toast.error('Failed to redo try-on');
        }
    };

    if (!isOpen) return null;

    const isProcessing = !!jobId;
    const hasEnoughCredits = balance === null || balance >= VTO_COST;
    const personImgUrl = selectedPersonImage?.image?.url || selectedPersonImage?.removedBgImage?.url || selectedPersonImage?.originalImageUrl;

    return ReactDOM.createPortal(
        <>
            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file);
                    e.target.value = '';
                }}
            />

            {/* Full-screen overlay */}
            <div
                ref={overlayRef}
                onClick={handleOverlayClick}
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    zIndex: 9990,
                    background: 'rgba(0,0,0,0.55)',
                    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '16px'
                }}
            >
                <style>{`
                    @keyframes vtoSlideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                    @keyframes vtoSpin { to { transform: rotate(360deg); } }
                `}</style>

                <div style={{
                    background: 'white', borderRadius: '20px', maxWidth: '680px', width: '100%',
                    maxHeight: '80vh', overflow: 'hidden', position: 'relative',
                    boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
                    animation: 'vtoSlideUp 0.3s ease',
                    display: 'flex', flexDirection: 'column'
                }}>
                    {/* Header */}
                    <div style={{
                        padding: '14px 20px', borderBottom: '1px solid #f3f4f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexShrink: 0
                    }}>
                        <span style={{ fontSize: '15px', fontWeight: 600, color: '#111' }}>Virtual Try-On</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            {isLoggedIn && !loadingCredits && balance !== null && (
                                <span style={{ fontSize: '13px', color: balance > 0 ? '#14b8a6' : '#ef4444', fontWeight: 500 }}>
                                    {balance} credits
                                </span>
                            )}
                            <button onClick={handleClose} style={{
                                background: 'none', border: 'none', cursor: 'pointer',
                                fontSize: '18px', color: '#9ca3af', lineHeight: 1, padding: '4px'
                            }}>&#x2715;</button>
                        </div>
                    </div>

                    {/* Loading existing results overlay */}
                    {loadingExisting && !vtoResult && !selectedExistingResult && (
                        <div style={{
                            position: 'absolute', inset: 0, zIndex: 10,
                            background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <div style={{
                                width: '44px', height: '44px', border: '3px solid #e5e7eb',
                                borderTopColor: '#14b8a6', borderRadius: '50%',
                                animation: 'vtoSpin 1s linear infinite'
                            }} />
                            <p style={{ marginTop: '14px', color: '#6b7280', fontSize: '14px' }}>Checking results...</p>
                        </div>
                    )}

                    {/* Processing overlay */}
                    {isProcessing && (
                        <div style={{
                            position: 'absolute', inset: 0, zIndex: 10,
                            background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(8px)',
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <div style={{
                                width: '44px', height: '44px', border: '3px solid #e5e7eb',
                                borderTopColor: '#14b8a6', borderRadius: '50%',
                                animation: 'vtoSpin 1s linear infinite'
                            }} />
                            <p style={{ marginTop: '14px', color: '#6b7280', fontSize: '14px' }}>Generating your look...</p>
                        </div>
                    )}

                    {/* Scrollable content */}
                    <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                        {(vtoResult || selectedExistingResult) ? (
                            <VTOResultPanel
                                result={selectedExistingResult || vtoResult}
                                personImageUrl={selectedExistingResult?.personImageUrl || personImgUrl}
                                garmentImageUrl={garmentImageUrl}
                                onTryNewPhoto={handleTryNewPhoto}
                                onClose={handleClose}
                                cached={selectedExistingResult ? true : vtoResult?.cached}
                                onRedo={handleRedoTryOn}
                                vtoCost={VTO_COST}
                                product={product}
                                onPurchaseLicense={onPurchaseLicense}
                                hasLicense={hasLicense}
                            />
                        ) : hasExistingResults ? (
                            <ExistingResultsGrid
                                results={existingResults}
                                vtoCost={VTO_COST}
                                onSelectResult={setSelectedExistingResult}
                                onTryNewPhoto={handleTryNewPhoto}
                                product={product}
                                hasLicense={hasLicense}
                            />
                        ) : (
                            /* ===== TRY-ON LAYOUT ===== */
                            <div>
                                {/* Back button when coming from existing results */}
                                {forceNewTryOn && existingResults.length > 0 && (
                                    <button
                                        onClick={handleBackToResults}
                                        style={{
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            display: 'flex', alignItems: 'center', gap: '4px',
                                            fontSize: '13px', color: '#6b7280', padding: '0 0 12px 0'
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                                        </svg>
                                        Back to results
                                    </button>
                                )}

                                {/* Person + Garment side by side */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                    {/* PERSON IMAGE */}
                                    <div style={{ flex: 1 }}>
                                        <div
                                            onClick={handlePersonAreaClick}
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            style={{
                                                aspectRatio: '3/4', borderRadius: '16px',
                                                border: isDragging ? '2px dashed #14b8a6' : '2px dashed #3bbaa8',
                                                background: isDragging ? '#f0fdfa' : '#fff',
                                                display: 'flex', flexDirection: 'column',
                                                alignItems: 'center', justifyContent: 'center',
                                                cursor: 'pointer', overflow: 'hidden',
                                                position: 'relative', transition: 'all 0.2s'
                                            }}
                                        >
                                            {isUploading ? (
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                    <div style={{
                                                        width: '32px', height: '32px', border: '3px solid #e5e7eb',
                                                        borderTopColor: '#14b8a6', borderRadius: '50%',
                                                        animation: 'vtoSpin 1s linear infinite'
                                                    }} />
                                                    <span style={{ fontSize: '12px', color: '#14b8a6' }}>Uploading...</span>
                                                </div>
                                            ) : personImgUrl ? (
                                                <img src={personImgUrl} alt="Your photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <>
                                                    <PersonPlaceholderSVG size={48} />
                                                    <span style={{ fontSize: '12px', color: '#aaa', marginTop: '8px' }}>
                                                        {loadingImages ? 'Loading...' : 'Upload photo'}
                                                    </span>
                                                </>
                                            )}
                                            {personImgUrl && !isUploading && (
                                                <div style={{
                                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                                    padding: '6px', background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
                                                    textAlign: 'center'
                                                }}>
                                                    <span style={{ fontSize: '11px', color: 'white', fontWeight: 500 }}>Tap to change</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* PLUS SIGN */}
                                    <div style={{ width: '28px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round">
                                            <path d="M12 5v14" /><path d="M5 12h14" />
                                        </svg>
                                    </div>

                                    {/* GARMENT IMAGE */}
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            aspectRatio: '3/4', borderRadius: '16px',
                                            border: '2px dashed #e5e7eb', background: '#fafafa',
                                            overflow: 'hidden',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                                        }}>
                                            {garmentImageUrl ? (
                                                <img src={garmentImageUrl} alt="Garment" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                                    <path d="M16 8 L24 4 L32 8 L36 16 L32 16 L30 12 L30 40 L18 40 L18 12 L16 16 L12 16 Z" fill="none" stroke="#d1d5db" strokeWidth="2" />
                                                </svg>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Person image picker - horizontal scroll row */}
                                {isLoggedIn && personImages.length > 0 && (
                                    <div style={{
                                        marginBottom: '16px', display: 'flex', gap: '8px',
                                        overflowX: 'auto', paddingBottom: '4px',
                                        WebkitOverflowScrolling: 'touch'
                                    }}>
                                        {personImages.map(img => (
                                            <div
                                                key={img.id}
                                                onClick={() => setSelectedPersonImage(img)}
                                                style={{
                                                    width: '56px', height: '72px', flexShrink: 0,
                                                    borderRadius: '8px', cursor: 'pointer', overflow: 'hidden',
                                                    border: selectedPersonImage?.id === img.id ? '2px solid #14b8a6' : '2px solid transparent',
                                                    opacity: selectedPersonImage?.id === img.id ? 1 : 0.6,
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <img
                                                    src={img.image?.url || img.removedBgImage?.url || img.originalImageUrl}
                                                    alt=""
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Try On button */}
                                <button
                                    onClick={handleStartTryOn}
                                    disabled={isProcessing || isStarting}
                                    style={{
                                        width: '100%', padding: '14px',
                                        background: '#14b8a6', color: 'white', border: 'none',
                                        borderRadius: '6px', fontSize: '15px', fontWeight: 600,
                                        cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                        opacity: (isProcessing || isStarting) ? 0.6 : 1,
                                        transition: 'opacity 0.2s'
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                                    </svg>
                                    {!isLoggedIn
                                      ? 'Sign in to Try On'
                                      : `Try On - ${VTO_COST} Credit${VTO_COST !== 1 ? 's' : ''}`}
                                </button>
                                {isLoggedIn && isSharedItem && sharerUsername && (
                                    <p style={{ textAlign: 'center', marginTop: '6px', fontSize: '12px', color: '#6b7280' }}>
                                        1 credit goes to @{sharerUsername}
                                    </p>
                                )}

                                {isLoggedIn && !hasEnoughCredits && (
                                    <p style={{ textAlign: 'center', marginTop: '8px', fontSize: '13px' }}>
                                        <button
                                            onClick={() => setIsCreditModalOpen(true)}
                                            style={{ background: 'none', border: 'none', color: '#14b8a6', cursor: 'pointer', fontSize: '13px', textDecoration: 'underline' }}
                                        >
                                            Get more credits
                                        </button>
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Credit Purchase Modal */}
            <CreditPurchaseModal
                isOpen={isCreditModalOpen}
                onClose={() => setIsCreditModalOpen(false)}
                currentBalance={balance}
                onSuccess={() => {
                    refetchCredits();
                }}
            />
        </>,
        document.body
    );
});

VirtualTryOnModal.displayName = 'VirtualTryOnModal';

export default VirtualTryOnModal;
